import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideDownload, lucideEyeOff, lucideHeart, lucideMessageCircle } from '@ng-icons/lucide';
import { QueuedImageDirective } from '@lite-workspace/shared/images';
import { HlmBadge } from '@lite-workspace/shared/helm-ui/badge';
import { HlmButton, buttonVariants } from '@lite-workspace/shared/helm-ui/button';
import { hlm } from '@lite-workspace/shared/helm-ui/utils';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import type { PublicGalleryPhoto } from './public-photo.models';
import { isRawName } from './is-raw-name';
import { pickPreviewUrl, previewVersionFor } from './pick-preview-url';

// A hint on a tile action only earns the screen after the pointer has come to rest on the glyph —
// a sweep across the strip must not trail four plates behind it (spec «Галерея клиента» п. 14).
const TILE_TOOLTIP_DELAY_MS = 1000;

// Too long for a template line, so the caption binds it through `[class]` beside its static list.
const GLYPH_SHADOW_CLASS =
  '[filter:drop-shadow(0_--spacing(0.25)_--spacing(0.5)_var(--scrim-ctrl-2))_drop-shadow(0_0_--spacing(1.5)_var(--scrim-ctrl))]';

@Component({
  selector: 'gallery-photo-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, QueuedImageDirective, HlmBadge, HlmButton, HlmTooltip],
  providers: [provideIcons({ lucideEyeOff, lucideHeart, lucideDownload, lucideMessageCircle })],
  // Inside a `break-inside-avoid` multi-column cell an inline host adds a baseline
  // gap under every tile — the shared photo-tile blocks its host for the same reason.
  host: { class: 'block' },
  template: `
    <figure class="group relative m-0 w-full">
      <button
        type="button"
        class="relative block w-full cursor-zoom-in overflow-hidden"
        [style.aspect-ratio]="aspectRatio()"
        [style.background-color]="photo().dominantColor"
        aria-label="Открыть фото"
        (click)="open.emit()"
      >
        @if (src(); as url) {
          <!-- The shared load queue (6 slots + blob cache) hands the frame over only once it has
               decoded, so a tile never flashes a half-painted picture. A failed load of a tile never
               painted leaves the dominant-colour box; a tile that already showed this photo keeps that
               frame when a re-signed address fails. The request itself waits until the
               tile is within one and a half screens (appQueuedImg), so opening a big gallery does not
               download all of it — until then the dominant-colour box is the placeholder. -->
          <img
            [appQueuedImg]="url"
            [alt]="''"
            decoding="async"
            draggable="false"
            class="absolute inset-0 h-full w-full object-cover transition-opacity duration-[var(--t-slow)]
              ease-[var(--ease-smooth)] motion-reduce:transition-none"
            [class.opacity-0]="!painted()"
            [class.opacity-30]="painted() && showPrivateBadge()"
            (loadedChange)="$event && paintedPhotoId.set(photo().id)"
            (loadError)="imageError.emit()"
          />
        } @else if (isRaw()) {
          <!-- RAW without previews: badge + filename instead of an empty tile. -->
          <span class="text-c2 absolute inset-0 flex flex-col items-center justify-center gap-1">
            <span hlmBadge variant="outline">RAW</span>
            <span class="max-w-full truncate px-2 text-[10px]">{{ photo().originalName }}</span>
          </span>
        }
        @if (showPrivateBadge()) {
          <span hlmBadge variant="on-media" class="absolute top-2 left-2">
            <ng-icon name="lucideEyeOff" size="16" class="opacity-60" aria-label="Скрыто от гостей" />
          </span>
        }
      </button>

      <!-- Hover/focus bottom actions: hidden at rest, revealed on hover + keyboard focus-within.
           No strip and no chips behind the glyphs: a two-layer drop shadow keeps the white glyphs
           legible on a bright photo as well as a dark one. Order, left to right:
           «Скачать» → глаз → комментарий → сердце; the client-only arms simply never render for a
           guest, leaving «Скачать» · сердце.
           A touch device has no hover, so the actions can never be revealed there: under pointer-coarse:
           ONLY the heart stays — a 44×44 scrim pill in the tile's bottom-right corner, legible on any
           photo (design rules mobile.md: touch target from padding, never a bigger glyph). -->
      @if (downloadable() || heartable() || noteable() || privatable()) {
        <figcaption
          class="absolute inset-x-0 bottom-0 flex items-center justify-end gap-4 p-2
            opacity-0 transition-opacity duration-[var(--t-fast)] ease-[var(--ease-smooth)] group-hover:opacity-100
            group-focus-within:opacity-100 motion-reduce:transition-none
            pointer-coarse:[filter:none] pointer-coarse:opacity-100"
          [class]="glyphShadowClass"
        >
          @if (downloadable()) {
            @if (downloadDisabledForScene()) {
              <!-- Explicit exclusion (spec 08 Q4): a grayed glyph + tooltip, never a silent drop. -->
              <span
                [class]="blockedDownloadClass"
                role="img"
                aria-label="Скачивание отключено для этой папки"
                hlmTooltip="Скачивание отключено для этой папки"
                [showDelay]="tooltipDelayMs"
              >
                <ng-icon name="lucideDownload" size="20" />
              </span>
            } @else {
              <button
                hlmBtn
                variant="on-media-icon"
                size="icon-xs"
                type="button"
                class="bg-transparent backdrop-blur-none hover:opacity-70 pointer-coarse:hidden"
                aria-label="Скачать фото"
                hlmTooltip="Скачать фото"
                [showDelay]="tooltipDelayMs"
                (click)="download.emit()"
              >
                <ng-icon name="lucideDownload" size="20" />
              </button>
            }
          }
          @if (privatable()) {
            <button
              hlmBtn
              variant="on-media-icon"
              size="icon-xs"
              type="button"
              class="bg-transparent backdrop-blur-none hover:opacity-70 pointer-coarse:hidden"
              [class.text-acc2]="photo().isClientPrivate"
              [attr.aria-label]="photo().isClientPrivate ? 'Показать гостям' : 'Скрыть от гостей'"
              [attr.aria-pressed]="photo().isClientPrivate"
              [hlmTooltip]="photo().isClientPrivate ? 'Снова показать гостям' : 'Скрыть от гостей'"
              [showDelay]="tooltipDelayMs"
              (click)="togglePrivate.emit()"
            >
              <ng-icon name="lucideEyeOff" size="20" />
            </button>
          }
          @if (noteable()) {
            <button
              hlmBtn
              variant="on-media-icon"
              size="icon-xs"
              type="button"
              class="bg-transparent backdrop-blur-none hover:opacity-70 pointer-coarse:hidden"
              [class.text-acc2]="hasNote()"
              [attr.aria-label]="hasNote() ? 'Редактировать комментарий' : 'Комментировать фото'"
              [hlmTooltip]="hasNote() ? 'Редактировать комментарий' : 'Комментировать фото'"
              [showDelay]="tooltipDelayMs"
              (click)="note.emit()"
            >
              <ng-icon name="lucideMessageCircle" size="20" [style.fill]="hasNote() ? 'currentColor' : 'none'" />
            </button>
          }
          @if (heartable()) {
            <button
              hlmBtn
              variant="on-media-icon"
              size="icon-xs"
              type="button"
              class="bg-transparent backdrop-blur-none hover:opacity-70 pointer-coarse:size-11
                pointer-coarse:bg-[var(--scrim-ctrl-2)] pointer-coarse:backdrop-blur-sm"
              [class.text-bad]="hearted()"
              [attr.aria-label]="hearted() ? 'Убрать из избранного' : 'В избранное'"
              [attr.aria-pressed]="hearted()"
              [hlmTooltip]="hearted() ? 'Убрать из избранного' : 'В избранное'"
              [showDelay]="tooltipDelayMs"
              [disabled]="heartPending()"
              (click)="heart.emit()"
            >
              <ng-icon name="lucideHeart" size="20" [style.fill]="hearted() ? 'currentColor' : 'none'" />
            </button>
          }
        </figcaption>
      }
    </figure>
  `,
})
export class GalleryPhotoCardComponent {
  readonly photo = input.required<PublicGalleryPhoto>();
  // CSS width of the cell this tile lands in — the rendition rung is picked per photo, because a
  // portrait frame is narrower than its rung's nominal (long-side) number.
  readonly cellWidth = input.required<number>();
  // Multiplied into the screen density when picking the rung: a host that shrinks the whole tree by
  // a transform shows the cell smaller than its CSS width says (spec «Дизайн галереи» item 50).
  readonly densityScale = input(1);
  readonly clientRole = input(false);
  // Hover-bar affordances: absent = the bar arm never renders.
  readonly heartable = input(false);
  readonly hearted = input(false);
  readonly heartPending = input(false);
  readonly downloadable = input(false);
  // Scene excluded from downloads: the glyph renders disabled with a tooltip.
  readonly downloadDisabledForScene = input(false);
  // Client-only note affordance: absent = no comment glyph on the bar.
  readonly noteable = input(false);
  readonly hasNote = input(false);
  // Client-only privacy affordance: hide from guests / restore (ADR-0027).
  readonly privatable = input(false);
  readonly open = output<void>();
  readonly heart = output<void>();
  readonly download = output<void>();
  readonly note = output<void>();
  readonly togglePrivate = output<void>();
  // The picture failed to load (an expired signed URL, most often) — the host may re-read its source.
  readonly imageError = output<void>();

  protected readonly tooltipDelayMs = TILE_TOOLTIP_DELAY_MS;
  protected readonly glyphShadowClass = GLYPH_SHADOW_CLASS;

  // The excluded-scene glyph keeps a live tooltip, so it stays a `<span role="img">` — a disabled
  // button fires no pointer events. It still wears the neighbours' box: the same cva call the
  // `hlmBtn` arms make, merged with the disabled tone, never a hand-copied class string.
  protected readonly blockedDownloadClass = hlm(
    buttonVariants({ variant: 'on-media-icon', size: 'icon-xs' }),
    'bg-transparent backdrop-blur-none cursor-not-allowed opacity-40 pointer-coarse:hidden',
  );

  // Keyed on the photo, not the URL: a re-signed URL of the same photo loads over the frame already
  // painted instead of blanking the tile; another photo in this tile starts from the placeholder.
  protected readonly paintedPhotoId = signal<string | null>(null);
  protected readonly painted = computed(() => this.paintedPhotoId() === this.photo().id);

  // width / height; unknown while the fleet is still processing — 3:2 landscape, the same default
  // the column packer assumes, so the reserved box and the packed column agree.
  private readonly ratio = computed(() => {
    const { width, height } = this.photo();
    return width && height ? width / height : 1.5;
  });

  protected readonly aspectRatio = computed(() => {
    const { width, height } = this.photo();
    // 3:2 box while the fleet is still processing — keeps masonry stable.
    return width && height ? `${width} / ${height}` : '3 / 2';
  });

  protected readonly src = computed(() =>
    pickPreviewUrl(
      this.photo(),
      previewVersionFor(
        this.cellWidth(),
        (typeof devicePixelRatio === 'number' ? devicePixelRatio : 1) * this.densityScale(),
        this.ratio(),
      ),
    ),
  );

  protected readonly isRaw = computed(() => isRawName(this.photo().originalName));

  protected readonly showPrivateBadge = computed(() => this.clientRole() && this.photo().isClientPrivate);
}
